import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {OrderCarpet} from '@domain/planning-prototype/carpet/order-carpet';
import {ProductionOrder} from '@domain/planning-prototype/carpet/production-order';
import {GenericOrder} from '@domain/planning-prototype/generic-order.interface';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {PlanningItemType} from '@domain/planning-prototype/planning-item-type';
import {ProductionOrderPlastic} from '@domain/planning-prototype/plastic/production-order-plastic';
import {ProductionOrderToPlan} from '@domain/planning-prototype/to-plan/production-order-to-plan';
import {ProductionOrderTuft} from '@domain/planning-prototype/tufting/production-order-tuft';
import {ProductionOrderWeaving} from '@domain/planning-prototype/weaving/production-order-weaving';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  GridOptionsBuilderFactoryService,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  SelectGridDialog,
  StringUtils
} from '@vdw/angular-component-library';
import {ColDef, GetRowIdParams, GridApi, GridOptions, GridReadyEvent} from 'ag-grid-community';
import {PlanningPrototypeContextService} from '../../../utilities/planning-prototype-context.service';
import {SelectUnplannedOrderParameters} from './select-unplanned-order-parameters';

@Component({
  selector: 'app-select-unplanned-order',
  templateUrl: './select-unplanned-order.component.html'
})
export class SelectUnplannedOrderComponent extends BaseComponent implements SelectGridDialog, OnInit {
  public listOfGridOptions: GridOptions<ProductionOrderToPlan>[] = [];
  public listOfGridApis: GridApi<ProductionOrderToPlan>[] = [];
  public listOfUnplannedOrders: ProductionOrderToPlan[] = [];

  public constructor(
    private readonly gridOptionsBuilder: GridOptionsBuilderFactoryService,
    private readonly colDefBuilder: ColDefBuilderFactoryService,
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly dialogRef: MatDialogRef<SelectUnplannedOrderComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly dialogParams: SelectUnplannedOrderParameters
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setGridOptions();
    this.initListOfOrdersToPlan();
  }

  public confirmSelection(): void {
    const selectedRows = this.listOfGridApis[0].getSelectedRows();
    this.dialogRef.close(selectedRows);
  }

  public canSelectProductionOrders(): boolean {
    return this.listOfGridApis[0]?.getSelectedRows()?.length > 0;
  }

  private initListOfOrdersToPlan(): void {
    const productionOrderIdsOnPlanningDraft = this.getListOfProductionOrderIdsAlreadyPlanned();

    this.listOfUnplannedOrders = this.planningContext.ordersToPlan.filter(
      (order: ProductionOrderToPlan) => this.unplannedOrderMatchesDialogParams(order) && !productionOrderIdsOnPlanningDraft.includes(order.productionOrder.id)
    );
  }

  private getListOfProductionOrderIdsAlreadyPlanned(): number[] {
    const flatListOfPlanningItems = this.planningContext.getFlatListOfPlanningItems();
    const planningItemsInRun = flatListOfPlanningItems.find((item: PlanningItem) => item.draftId === this.dialogParams.parentItemId)?.planningItems ?? [];

    const isCompatibleItemType =
      this.dialogParams.type === PlanningItemType.ORDER_CARPET
        ? (item: PlanningItem): boolean => item instanceof OrderCarpet && !AssertionUtils.isNullOrUndefined(item.productionOrder)
        : (item: PlanningItem): boolean => PlanningItemHelper.isOrderLiteItem(item);

    return flatListOfPlanningItems
      .filter((item: PlanningItem) => isCompatibleItemType(item) && !planningItemsInRun.includes(item))
      .concat(this.dialogParams.itemsSelectedAlready.filter(PlanningItemHelper.isOrderItem))
      .map((order: GenericOrder) => order.productionOrder.id);
  }

  private unplannedOrderMatchesDialogParams(order: ProductionOrderToPlan): boolean {
    if (order.productionOrder instanceof ProductionOrder) {
      return (
        this.dialogParams.type === PlanningItemType.ORDER_CARPET &&
        this.dialogParams.creel?.id === order.productionOrder.creel.id &&
        this.dialogParams.machineQuality?.id === order.productionOrder.quality.id
      );
    }

    if (order.productionOrder instanceof ProductionOrderWeaving) {
      return this.dialogParams.type === PlanningItemType.ORDER_WEAVING && this.dialogParams.product?.id === order.productionOrder.weaveProduct.id;
    }

    if (order.productionOrder instanceof ProductionOrderTuft) {
      return this.dialogParams.type === PlanningItemType.ORDER_TUFTING && this.dialogParams.product?.id === order.productionOrder.tuftProduct.id;
    }

    if (order.productionOrder instanceof ProductionOrderPlastic) {
      return this.dialogParams.type === PlanningItemType.ORDER_PLASTIC && this.dialogParams.product?.id === order.productionOrder.tool?.id;
    }

    return false;
  }

  private setGridOptions(): void {
    this.listOfGridOptions = [
      this.gridOptionsBuilder
        .getBuilder(this.getColumnDefs(), GridIdentifier.SELECT_PRODUCTION_ORDER)
        .withOnGridReady((ready: GridReadyEvent) => this.listOfGridApis.push(ready.api))
        .withColumnMenu()
        .withGetRowId((params: GetRowIdParams<ProductionOrderToPlan>) => params.data.productionOrder.id.toString())
        .withRowSelection(true)
        .withNoRowsOverlay({
          scale: 0.7,
          titleKey: 'PLANNING.ADD_ITEM.SELECT_PRODUCTION_ORDERS.NO_PRODUCTION_ORDERS_FOR_RUN',
          hideDescription: true
        } as Partial<NoDataOverlayComponentParams>)
        .withLoadingOverlay({
          scale: 0.7
        } as OverlayComponentParams)
        .build()
    ];
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilder.getBuilder().withField('productionOrder.name').withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilder.getBuilder().withField('productionOrder.dueDate').withHeaderName('GENERAL.DATETIME.DUE_DATE').withDate().withoutFilter().build()
    ];
  }
}
